import axios from 'axios';
import { reportApiError } from '@/sentryApi';
import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import { TokenService } from './token';
import { useAppStore } from '@/stores/app';
import { useAuthStore } from '@/stores/auth';

// `process.env.NODE_ENV` is statically replaced by Vite's define at build time;
// the identifier is declared here because @types/node is not part of the app tsconfig.
declare const process: { env: Record<string, string | undefined> };

/** Normalized error shape returned by {@link ApiServiceV2.formatError} on failed requests. */
export interface ApiV2Error {
	/** Machine-readable error code, e.g. an axios code like 'ECONNABORTED', or 'UNKNOWN_ERROR' as fallback. */
	code: string;
	/** Human-readable description of what went wrong. */
	message: string;
	/** HTTP status code of the failed response; undefined when the request never reached the server. */
	status?: number;
	/** Raw response body from the server (if any) for debugging; null when unavailable. */
	details?: any;
}

/** Failure envelope returned by every request helper instead of throwing. */
export interface ApiV2Failure {
	/** Always false — discriminator so callers can check `res.success === false`. */
	success: false;
	/** The normalized error describing the failure. */
	error: ApiV2Error;
}

const axiosInstance: AxiosInstance = axios.create({
	headers: {
		'Content-Type': 'application/json',
	},
	timeout: 60000,
});

// Add a request interceptor to set the token before each request
axiosInstance.interceptors.request.use(
	(config) => {
		const token = TokenService.getToken();
		if (token) {
			config.headers['Authorization'] = `Bearer ${token}`;
		}
		return config;
	},
	(error) => {
		return Promise.reject(error);
	}
);

class ApiServiceV2 {
	/** Interceptor ID returned by axios when the 401 interceptor is mounted; null when unmounted. */
	_401interceptor: number | null;

	constructor() {
		this._401interceptor = null; // 401 interceptor
		// Mount the interceptor by default
		this.mount401Interceptor();
	}

	get baseURL(): string | undefined {
		return axiosInstance.defaults.baseURL;
	}

	init(baseURL: string): void {
		axiosInstance.defaults.baseURL = baseURL;

		// if environment is not production, set x-moinai-dev header
		if (process.env.NODE_ENV !== 'production') {
			// NOTE: reads directly off import.meta (not import.meta.env) — kept as-is for identical runtime behavior
			axiosInstance.defaults.headers.common['x-moinai-dev'] = (import.meta as any).VITE_APP_HEADER_SECRET;
			}
	}

	/** Performs an arbitrary axios request; resolves with the response body, or a failure envelope instead of throwing. */
	async customRequest(config: AxiosRequestConfig): Promise<any | ApiV2Failure> {
		try {
			const res = await axiosInstance(config);
			return res.data;
		} catch (error: any) {
			return { success: false, error: this.formatError(error) };
		}
	}

	async get(url: string, config: AxiosRequestConfig = {}): Promise<any | ApiV2Failure> {
		try {
			const res = await axiosInstance.get(url, config);
			return res.data;
		} catch (error: any) {
			return { success: false, error: this.formatError(error) };
		}
	}

	async post(url: string, data?: any, config: AxiosRequestConfig = {}): Promise<any | ApiV2Failure> {
		try {
			const res = await axiosInstance.post(url, data, config);
			return res.data;
		} catch (error: any) {
			return { success: false, error: this.formatError(error) };
		}
	}

	async put(url: string, data?: any, config: AxiosRequestConfig = {}): Promise<any | ApiV2Failure> {
		try {
			const res = await axiosInstance.put(url, data, config);
			return res.data;
		} catch (error: any) {
			return { success: false, error: this.formatError(error) };
		}
	}

	async patch(url: string, data?: any, config: AxiosRequestConfig = {}): Promise<any | ApiV2Failure> {
		try {
			const res = await axiosInstance.patch(url, data, config);
			return res.data;
		} catch (error: any) {
			return { success: false, error: this.formatError(error) };
		}
	}

	async delete(url: string, config: AxiosRequestConfig = {}): Promise<any | ApiV2Failure> {
		try {
			const res = await axiosInstance.delete(url, config);
			return res.data;
		} catch (error: any) {
			return { success: false, error: this.formatError(error) };
		}
	}

	mount401Interceptor(): void {
		// Skip if interceptor is already mounted
		if (this._401interceptor !== null) {
			return;
		}

		this._401interceptor = axiosInstance.interceptors.response.use(
			(response) => response,
			async (error) => {
				// 5xx → Sentry (no-op without a DSN); before any of the handling below reshapes the error.
				reportApiError(error);

				// if version txt file fetching failed, do nothing
				if (error && error.config && error.config.url === `${(import.meta as any).VITE_APP_HUB_URL}/version.txt`) {
					return Promise.reject(error);
				}

				// Call "await this.get(`/bot`)" will change the Bot if we receive Error from backend request.
				// After Error Middleware has been added to back end. We can handle code errors here:
				// error.response.data should have { code: number, message: string, status: httpStatus }
				if (error.response?.data) {
					const { code, message, status } = error.response.data;

					// Return direct response when we have all 3 parameters
					if (error.response.data?.hasOwnProperty('code') && message && status) {
						throw error.response.data;
					}
				}

				if (!error.response) {
					if (!axios.isCancel(error)) setTimeout(() => useAppStore().handleConnectionLost(), 1.5 * 1000);
					return Promise.reject(error);
				} else if (error.response.status === 401) {
					if (error.config?.url === `/bot`) {
						// Logout the user
						useAuthStore().logout();
						throw error;
					} else {
						await this.get(`/bot`);
						// Retry the original request
					}
				}

				// If error wasn't handled, just reject as is
				return Promise.reject(error);
			}
		);
	}

	unmount401Interceptor(): void {
		// Eject the interceptor
		axiosInstance.interceptors.response.eject(this._401interceptor as number);
	}

	/** Converts any thrown axios/server error into the stable {@link ApiV2Error} shape. */
	formatError(error: any): ApiV2Error {
		// If the server returned a response with an error object, use that directly
		if (error.response &&
			error.response.data &&
			error.response.data.success === false) {
			return error.response.data.error;
		}

		// Fallback to generic error format
		return {
			code: error.code || 'UNKNOWN_ERROR',
			message: error.message || 'An unknown error occurred',
			status: error.response?.status,
			details: error.response?.data || null,
		};
	}
}

// Create singleton instance
const apiServiceInstance = new ApiServiceV2();

// Initialize with the base URL if environment variable is set
if ((import.meta as any).VITE_APP_API_URL) {
	apiServiceInstance.init((import.meta as any).VITE_APP_API_URL);
}

export default apiServiceInstance;
